import { useEffect, useMemo, useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import WidgetPlaceholder from "./WidgetPlaceholder";
import { findSentimentLevel } from "./sentimentScale";
import DownloadOptionsMenu, {
  type WidgetDownloadOptions,
} from "./DownloadOptionsMenu";

// ─── Types ────────────────────────────────────────────────────────────────────

type SeriesPoint = { x: string; y: number };

type SeriesInput = {
  name: string;
  data?: SeriesPoint[];
};

type Metadata = {
  period?: string;
};

type Props = {
  isLoading: boolean;
  data: unknown;
  title?: string;
  dashboardName?: string;
  widgetTemplateId?: string | number;
  appliedFilters?: Record<string, unknown>;
};

// ─── Colors ───────────────────────────────────────────────────────────────────

// Series here are sentiment buckets (Positive / Neutral / Mixed / Negative,
// sometimes combined like "Neutral / Mixed") — route through the shared
// scale so this chart never shows a different color for the same category
// than DonutPieWidget/SentimentSplitWidget do on the same dashboard.
// Anything the scale doesn't recognize gets a fallback color.
const FALLBACK_COLORS = ["#2563EB", "#D97706", "#7C3AED", "#0D9488"];

const colorForSeries = (name: string, fallbackIndex: number): string =>
  findSentimentLevel(name)?.color ??
  FALLBACK_COLORS[fallbackIndex % FALLBACK_COLORS.length] ??
  "#64748B";

// ─── Data unwrap ──────────────────────────────────────────────────────────────

type UnwrapResult = {
  series: SeriesInput[];
  metadata: Metadata;
  // Siblings of .data, not nested under .metadata — same envelope shape as
  // CustomBarWidget/InfoBarWidget's own unwrap.
  hasDownloadOptions?: boolean;
  downloadOptions?: WidgetDownloadOptions;
};

// Accepts either the full API response ({ data: series[], metadata }) or the
// bare series array (WidgetRenderer strips the envelope before passing data
// down).
const unwrap = (raw: unknown): UnwrapResult => {
  if (Array.isArray(raw)) return { series: raw as SeriesInput[], metadata: {} };
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.data)) {
      return {
        series: obj.data as SeriesInput[],
        metadata: (obj.metadata ?? {}) as Metadata,
        hasDownloadOptions: obj.has_download_options as boolean | undefined,
        downloadOptions: obj.download_options as
          | WidgetDownloadOptions
          | undefined,
      };
    }
  }
  return { series: [], metadata: {} };
};

/** "2026-06-30" → "Jun 30". Falls back to the raw string for anything else
 *  (weekly/monthly period labels, or a malformed date) — the period this
 *  chart buckets by is caller-defined (day/week/month/quarter) and not
 *  always a plain ISO day. */
const formatAxisLabel = (raw: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!match) return raw;
  const [, y, m, d] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// ─── Component ────────────────────────────────────────────────────────────────

// chart_type: "single_stack_bar" — one stacked column per period (as opposed
// to grouped_bar's side-by-side columns), e.g. "Call Volume Over Time by
// Sentiment". Always stacked — that's the point of this chart type — unlike
// LineWithBarWidget (chart_type "line_with_bar"), which stays a separate,
// untouched component for genuine mixed line+column combos.
export default function SingleStackBarWidget({
  isLoading,
  data,
  title,
  dashboardName,
  widgetTemplateId,
  appliedFilters,
}: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const { series, hasDownloadOptions, downloadOptions } = useMemo(
    () => unwrap(data),
    [data],
  );

  // exporting is disabled below (no native Highcharts menu to merge into),
  // so this is a standalone portal menu — same pattern as CustomBarWidget/
  // SentimentSplitWidget, not ChartFactory's merge-into-native-menu path.
  const downloadMenu = (
    <DownloadOptionsMenu
      menuId={
        widgetTemplateId !== undefined ? String(widgetTemplateId) : undefined
      }
      title={title}
      dashboardName={dashboardName}
      widgetTemplateId={widgetTemplateId}
      appliedFilters={appliedFilters}
      downloadOptions={hasDownloadOptions ? downloadOptions : undefined}
    />
  );

  // Union of every series' x values, in first-seen order — series aren't
  // guaranteed to share identical x arrays, so categories are built from
  // whichever points actually exist rather than assumed from series[0].
  const categories = useMemo(() => {
    const seen = new Set<string>();
    const ordered: string[] = [];
    for (const s of series) {
      for (const point of s.data ?? []) {
        const x = String(point.x);
        if (!seen.has(x)) {
          seen.add(x);
          ordered.push(x);
        }
      }
    }
    return ordered;
  }, [series]);

  const highchartsSeries = useMemo<Highcharts.SeriesOptionsType[]>(
    () =>
      series.map((s, i) => {
        const byX = new Map((s.data ?? []).map((p) => [String(p.x), p.y]));
        return {
          type: "column",
          name: s.name,
          color: colorForSeries(s.name, i),
          data: categories.map((x) => byX.get(x) ?? null),
        } as Highcharts.SeriesOptionsType;
      }),
    [series, categories],
  );

  // Same container-sizing approach as DonutPieWidget:
  // chart.reflow() can't resolve height:100% this many flex levels deep and
  // falls back to Highcharts' hardcoded 400px default, so measure the
  // wrapper directly.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    let raf = 0;
    const resize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const chart = chartRef.current?.chart;
        if (!chart || !el) return;
        chart.setSize(el.clientWidth, el.clientHeight, false);
      });
    };
    const timer = setTimeout(resize, 0);
    const ro = new ResizeObserver(resize);
    ro.observe(el);
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [isLoading, highchartsSeries.length]);

  const options = useMemo<Highcharts.Options>(
    () => ({
      chart: {
        type: "column",
        animation: false,
        backgroundColor: "transparent",
        style: { fontFamily: "inherit" },
      },
      title: { text: "" },
      credits: { enabled: false },
      exporting: { enabled: false },
      accessibility: { enabled: false },
      xAxis: {
        // Categorical, not datetime — the caller buckets by day/week/month/
        // quarter and the payload's x values already reflect that (e.g. the
        // day-level example here skips weekends), so a continuous datetime
        // axis would draw misleading gaps for the missing days.
        categories,
        lineColor: "#E2E8F0",
        labels: {
          style: { fontSize: "11px", color: "#334155" },
          formatter() {
            return formatAxisLabel(String(this.value));
          },
        },
      },
      yAxis: {
        min: 0,
        // Column stacking defaults to putting the FIRST series on top of the
        // stack (Highcharts' reversedStacks defaults to true for columns) —
        // reversed here so series order (e.g. Positive, Neutral/Mixed,
        // Negative) reads bottom-to-top, matching the reference design.
        reversedStacks: false,
        gridLineColor: "#f0f0f0",
        title: { text: undefined },
        labels: { style: { fontSize: "11px", color: "#6c757d" } },
      },
      legend: {
        enabled: true,
        itemStyle: { fontSize: "11px", fontWeight: "normal" },
      },
      tooltip: {
        shared: true,
        formatter() {
          const points = (this.points ?? []) as Highcharts.Point[];
          // const header = formatAxisLabel(String(this.x));
          const rows = points
            .map(
              (p) =>
                `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${p.y}</b>`,
            )
            .join("<br/>");
          return `<div style="font-size:11px;"><br/>${rows}</div>`;
        },
      },
      plotOptions: {
        column: {
          stacking: "normal",
          borderWidth: 0,
          animation: false,
        },
        series: {
          animation: false,
        },
      },
      series: highchartsSeries,
    }),
    [categories, highchartsSeries],
  );

  if (isLoading || data === undefined || data === null) {
    return <WidgetPlaceholder height={280} />;
  }

  if (series.length === 0 || categories.length === 0) {
    return (
      <>
        {downloadMenu}
        <div
          className="d-flex align-items-center justify-content-center h-100"
          style={{ fontSize: 12.5, color: "var(--bs-secondary-color)" }}
        >
          No trend data available.
        </div>
      </>
    );
  }

  return (
    <div className="flex-fill h-100" style={{ minHeight: 0 }} ref={wrapperRef}>
      {downloadMenu}
      <HighchartsReact
        ref={chartRef}
        highcharts={Highcharts}
        options={options}
        containerProps={{ style: { width: "100%", height: "100%" } }}
      />
    </div>
  );
}
